import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { AbstractControl, ControlContainer, FormControl, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe, DatePipe } from '@angular/common';
import { Observable, Subscription, debounceTime, distinctUntilChanged, map, merge, of, switchMap, tap } from 'rxjs';
import { SharedModule } from '../../../core/shared.module';
import { LocalizzazioneService } from '../../../service/localizzazione.service';
import * as _ from 'lodash';
import { UtenteProvisioningService } from '../../../service/utenteProvisioning.service';
import { AutocompleteItem, ItAutocompleteComponent, ItInputComponent, ItSelectComponent } from 'design-angular-kit';

@Component({
  selector: 'app-utente',
  standalone: true,
  imports: [
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    AsyncPipe,
    ItInputComponent,
    ItSelectComponent,
    ItAutocompleteComponent,
  ],
  templateUrl: './utente.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    { provide: ControlContainer, useExisting: FormGroupDirective },
  ],
})
export class UtenteComponent implements OnInit {
  private _editable = true;
  @Input() set editable(value: boolean) {
    this._editable = value;
    this.disableForm();
  }
  get editable() {
    return this._editable;
  }
  @Input() _formControl: FormControl = new FormControl();
  @Input() uoList: any[] = [];
  @Input() ruoliList: any[] = [];
  @Input() _list: any[] = [];
  _listFiltered: Observable<any[]> = of();

  minDate: string = new Date(1900, 0, 1).toISOString().split('T')[0];
  today: string = new Date().toISOString().split('T')[0];

  constructor(
    private parentForm: FormGroupDirective,
    private cd: ChangeDetectorRef,
    private datePipe: DatePipe,
    private localizzazioneService: LocalizzazioneService,
    private utenteProvisioningService: UtenteProvisioningService
  ) {}

  utenteProvisioningFormGroup = new FormGroup({});
  pageSubscriptions = new Subscription();
  minCharApiCall = 3;
  filteredOptions: any[] = [];

  @Input() set reset(value: boolean) {
    this.utenteProvisioningFormGroup.reset();
    this.utenteProvisioningFormGroup.markAsUntouched();
  }
  @Input() set editMode(value: boolean) {
    this.utenteProvisioningFormGroup.markAllAsTouched();
  }

  private _formArrayGroup: any = null;
  @Input() set formArrayGroup(formGroup: FormGroup) {
    if (formGroup) {
      this._formArrayGroup = formGroup;
    }
  }

  get formArrayGroup(): FormGroup {
    return this._formArrayGroup;
  }

  private _nome!: FormControl;
  @Input() set nome(value: FormControl) {
    if (value) {
      this._nome = value;
      this.utenteProvisioningFormGroup.addControl('nome', value);
    }
  }
  get nome(): FormControl {
    return this._nome;
  }
  private _cognome!: FormControl;
  @Input() set cognome(value: FormControl) {
    if (value) {
      this._cognome = value;
      this.utenteProvisioningFormGroup.addControl('cognome', value);
    }
  }
  get cognome(): FormControl {
    return this._cognome;
  }

  private _sesso!: FormControl;
  @Input() set sesso(value: FormControl) {
    if (value) {
      this._sesso = value;
      this.utenteProvisioningFormGroup.addControl('sesso', value);
    }
  }
  get sesso(): FormControl {
    return this._sesso;
  }

  private _luogoNascita!: FormControl;
  @Input() set luogoNascita(value: FormControl) {
    if (value) {
      this._luogoNascita = value;
      this.utenteProvisioningFormGroup.addControl('luogoNascita', value);
      this.utenteProvisioningFormGroup.addControl(
        'codiceFiscaleLuogoNascita',
        new FormControl(null)
      );
    }
  }
  get luogoNascita(): FormControl {
    return this._luogoNascita;
  }

  private _dataNascita!: FormControl;
  @Input() set dataNascita(value: FormControl) {
    if (value) {
      this._dataNascita = value;
      this.utenteProvisioningFormGroup.addControl('dataNascita', value);
    }
  }
  get dataNascita(): FormControl {
    return this._dataNascita;
  }

  private _codiceFiscale!: FormControl;
  @Input() set codiceFiscale(value: FormControl) {
    if (value) {
      this._codiceFiscale = value;
      this.utenteProvisioningFormGroup.addControl('codiceFiscale', value);
    }
  }
  get codiceFiscale(): FormControl {
    return this._codiceFiscale;
  }

  private _codiceFiscaleLength: number | undefined;
  @Input() set codiceFiscaleLength(value: number) {
    if (value) {
      this._codiceFiscaleLength = value;
    }
  }
  get codiceFiscaleLength(): number {
    return this._codiceFiscaleLength ? this._codiceFiscaleLength : 16;
  }

  private _email!: FormControl;
  @Input() set email(value: FormControl) {
    if (value) {
      this._email = value;
      this.utenteProvisioningFormGroup.addControl('email', value);
    }
  }
  get email(): FormControl {
    return this._email;
  }

  private _confermaEmail!: FormControl;
  @Input() set confermaEmail(value: FormControl) {
    if (value) {
      this._confermaEmail = value;
      this.utenteProvisioningFormGroup.addControl('confermaEmail', value);
    }
  }
  get confermaEmail(): FormControl {
    return this._confermaEmail;
  }

  private _telefono!: FormControl;
  @Input() set telefono(value: FormControl) {
    if (value) {
      this._telefono = value;
      this.utenteProvisioningFormGroup.addControl('telefono', value);
    }
  }
  get telefono(): FormControl {
    return this._telefono;
  }

  private _unitaOrganizzative!: FormControl;
  @Input() set unitaOrganizzative(value: FormControl) {
    if (value) {
      this._unitaOrganizzative = value;
      this.utenteProvisioningFormGroup.addControl('unitaOrganizzative', value);
    }
  }
  get unitaOrganizzative(): FormControl {
    return this._unitaOrganizzative;
  }

  private _ruoliUtente!: FormControl;
  @Input() set ruoliUtente(value: FormControl) {
    if (value) {
      this._ruoliUtente = value;
      this.utenteProvisioningFormGroup.addControl('ruoliUtente', value);
    }
  }
  get ruoliUtente(): FormControl {
    return this._ruoliUtente;
  }

  setFormGroup(parentForm: FormGroup) {
    if (this.formArrayGroup) {
      this.formArrayGroup.addControl(
        'utenteProvisioning',
        this.utenteProvisioningFormGroup
      );
    } else {
      parentForm.addControl(
        'utenteProvisioning',
        this.utenteProvisioningFormGroup
      );
    }
  }

  disableForm() {
    if (!this.editable) {
      this.utenteProvisioningFormGroup.disable({ emitEvent: false });
    }
  }

  formChanges() {
    this.onAnagraficaChange();
    this.emailMatchChanges();
  }

  ngOnInit(): void {
    const form = this.parentForm.form;
    this.setFormGroup(form);
    this.formChanges();
  }

  onAnagraficaChange() {
    if (this.codiceFiscale) {
      this.pageSubscriptions.add(
        this._registerOnCodiceFiscaleChange((value: any) => {
          if (value && this.editable) {
            this._codiceFiscaleCheckValidity();
          }
        })
      );

      if (this.nome) {
        this.pageSubscriptions.add(
          this._registerOnNomeChange((value: any) => {
            if (this.nome.dirty && this.editable) {
              this._resetCodiceFiscale();
            }
          })
        );
      }

      if (this.cognome) {
        this.pageSubscriptions.add(
          this._registerOnCognomeChange((value: any) => {
            if (this.cognome.dirty && this.editable) {
              this._resetCodiceFiscale();
            }
          })
        );
      }

      if (this.sesso) {
        this.pageSubscriptions.add(
          this._registerOnSessoChange((value: any) => {
            if (this.sesso.dirty && this.editable) {
              this._resetCodiceFiscale();
            }
          })
        );
      }

      if (this.dataNascita) {
        this.pageSubscriptions.add(
          this._registerOnDataNascitaChange((value: any) => {
            if ((!value || this.dataNascita.dirty) && this.editable) {
              this._resetCodiceFiscale();
            }
          })
        );
      }

      if (this.luogoNascita) {
        this.pageSubscriptions.add(
          this._registerOnLuogoNascitaChange((value: any) => {
            if (this.luogoNascita.dirty && this.editable) {
              this._resetCodiceFiscale();
            }
          })
        );
      }
    }
  }

  private _registerOnNomeChange(fn: any): void {
    this.nome.valueChanges.subscribe(fn);
  }

  private _registerOnCognomeChange(fn: any): void {
    this.cognome.valueChanges.subscribe(fn);
  }

  private _registerOnSessoChange(fn: any): void {
    this.sesso.valueChanges.subscribe(fn);
  }

  private _registerOnCodiceFiscaleChange(fn: any): void {
    this.codiceFiscale.valueChanges.subscribe(fn);
  }

  private _registerOnDataNascitaChange(fn: any): void {
    this.dataNascita.valueChanges.subscribe(fn);
  }

  private _registerOnLuogoNascitaChange(fn: any): void {
    this.luogoNascita.valueChanges.subscribe(fn);
  }

  private _codiceFiscaleCheckValidity() {
    if (
      !this.nome.value &&
      !this.cognome.value &&
      !this.dataNascita.value &&
      !this.sesso.value &&
      !this.luogoNascita.value
    ) {
      return;
    }

    if (!this.codiceFiscale.value) {
      this.codiceFiscale.setErrors({ required: true });
      return;
    }

    if (
      !this.luogoNascita.value ||
      !this.nome.value ||
      !this.cognome.value ||
      (!this.dataNascita.value && !this.dataNascita) ||
      !this.sesso.value ||
      !this.codiceFiscale.value ||
      (this.codiceFiscale.value &&
        this.codiceFiscaleLength === 16 &&
        this.codiceFiscale.value.length > this.codiceFiscaleLength)
    ) {
      this.codiceFiscale.setErrors({ pattern: true });
      return;
    }

    if (!this.dataNascita.value) return;

    const dataNascitaTransformed = this.datePipe.transform(
      this.dataNascita.value,
      'yyyyMMdd'
    );

    const utenteBaseInfo = {
      Nome: this.nome.value,
      Cognome: this.cognome.value,
      DataDiNascita: dataNascitaTransformed,
      CodiceFiscaleComune: this.luogoNascita.value,
      CodiceFiscalePersona: this.codiceFiscale.value,
      Genere: this.sesso.value,
    };

    if (utenteBaseInfo.CodiceFiscalePersona && utenteBaseInfo.CodiceFiscalePersona.length === 16) {
      this.pageSubscriptions.add(
        this.utenteProvisioningService
          .checkCodiceFiscaleValidity(utenteBaseInfo)
          .subscribe((result: any) => {
            result.isValid
              ? this.codiceFiscale.setErrors(null)
              : this.codiceFiscale.setErrors({
                pattern: true,
              });
            this.utenteProvisioningFormGroup.updateValueAndValidity();
            this.cd.detectChanges();
          })
      );
    }
  }

  private _filterOptions(value: string) {
    return this.localizzazioneService.getComuniFiscali(value).pipe(
      distinctUntilChanged(),
      debounceTime(300),
      map((response) =>
        _.uniqBy(
          response.filter((option: any) => {
            return (
              option['denominazione']
                .toLowerCase()
                .indexOf(value.toLowerCase()) === 0
            );
          }),
          'denominazione'
        )
      )
    );
  }
  regExpOnlyLetters = /^[a-zA-Z\-' *]*$/;

  autocompleteUsers$ = (
    search: string | null | undefined
  ): Observable<AutocompleteItem<any>[]> => {
    if (!search || search.length < this.minCharApiCall) {
      return of([]);
    }

    // API request for retrieve data, use `search` to filter data
    return <any>of(
      this._filterOptions(search).pipe(
        map((response: any) => {
          return response.map((item: any) => {
            return {
              value: item.denominazione,
              // label: item.denominazione,
              additionalData: item,
            };
          });
        }),
        tap((response: any) => {
          this.filteredOptions = response;
          const luogoNascita: string =
            this.luogoNascita && this.luogoNascita.value
              ? this.luogoNascita.value
              : '';
          if (!this.regExpOnlyLetters.test(luogoNascita)) {
            this.luogoNascita.setErrors({ error: true });
          } else {
            const isValidOption = this.filteredOptions.some(option => option.value === luogoNascita);
            if (!isValidOption) {
              this.luogoNascita.setErrors({ error: true });
            } else {
              this.luogoNascita.setErrors(null);
              const codiceFiscaleLuogoNascita = this.filteredOptions.find(
                (option) => option.value === luogoNascita
              )?.additionalData.codiceComuneFiscale ?? '';
              if (codiceFiscaleLuogoNascita) {
                (this.utenteProvisioningFormGroup.get('codiceFiscaleLuogoNascita') as AbstractControl).setValue(codiceFiscaleLuogoNascita);
              }
            }
          }
        })
      )
    ).pipe(switchMap((response: any) => response));
  };

  onAutocompleteSelected(item: AutocompleteItem): void {
    (this.utenteProvisioningFormGroup.get('codiceFiscaleLuogoNascita') as AbstractControl).setValue(item.additionalData.codiceComuneFiscale);
  }

  private _resetCodiceFiscale() {
    this.codiceFiscale.reset();
  }

  closeSelect(formControl: string) {
    this.utenteProvisioningFormGroup.get(formControl)?.setValue(null);
  }

  focusInput(formControl: string, elementId: string) {
    this.utenteProvisioningFormGroup.get(formControl)?.setValue('');
    const el = document.getElementById(elementId);
    if (el) {
      el.focus();
    }
  }

  emailMatchChanges() {
    this.pageSubscriptions.add(
      merge(this.email.valueChanges, this.confermaEmail.valueChanges).subscribe(
        () => {
          if (this.email.value !== this.confermaEmail.value) {
            this.confermaEmail.setErrors({ emailMatch: true });
          } else {
            this.confermaEmail.setErrors(null);
          }
        }
      )
    );
  }

  ngOnDestroy() {
    this.pageSubscriptions.unsubscribe();
  }
}
